import React, { useState } from 'react';
import { useApp } from '../context/PetParksContext';
import type { Park } from '../types';
import { REVIEW_TAGS } from '../types/community';
import { communityService } from '../services/communityService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Star, Loader2 } from 'lucide-react';
import { cn } from './ui/utils';
import { toast } from 'sonner';

interface WriteReviewModalProps {
  park: Park;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const WriteReviewModal: React.FC<WriteReviewModalProps> = ({
  park,
  open,
  onClose,
  onSuccess,
}) => {
  const { selectedPet, t } = useApp();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MIN_REVIEW_LENGTH = 20;
  const MAX_REVIEW_LENGTH = 1000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (rating === 0) {
      toast.error(t('reviewSelectRating'));
      return;
    }

    if (content.length < MIN_REVIEW_LENGTH) {
      toast.error(
        t('reviewMinCharacters', { count: MIN_REVIEW_LENGTH.toString() })
      );
      return;
    }

    if (content.length > MAX_REVIEW_LENGTH) {
      toast.error(
        t('reviewMaxCharacters', { count: MAX_REVIEW_LENGTH.toString() })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await communityService.submitReview({
        park_id: park.id,
        user_id: 'current-user-id', // TODO: Get from auth context
        pet_id: selectedPet?.id,
        rating,
        title: title || undefined,
        content,
        visit_date: visitDate || undefined,
        tags: selectedTags.map((tagId) => ({
          id: tagId,
          label: REVIEW_TAGS.find((t) => t.id === tagId)?.label || '',
        })),
      });

      toast.success(t('reviewSubmittedSuccess'));
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(t('reviewSubmitFailed'));
      console.error('Review submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setTitle('');
    setContent('');
    setVisitDate('');
    setSelectedTags([]);
    onClose();
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const getReviewTagLabel = (tagId: string, fallback: string) => {
    const keyMap: Record<string, string> = {
      clean: 'reviewTagClean',
      safe: 'reviewTagSafe',
      busy: 'reviewTagBusy',
      quiet: 'reviewTagQuiet',
      shady: 'reviewTagShady',
      'well-maintained': 'reviewTagWellMaintained',
      spacious: 'reviewTagSpacious',
      'friendly-dogs': 'reviewTagFriendlyDogs',
    };
    const key = keyMap[tagId];
    return key ? t(key as never) : fallback;
  };

  const characterCount = content.length;
  const isValid =
    rating > 0 &&
    characterCount >= MIN_REVIEW_LENGTH &&
    characterCount <= MAX_REVIEW_LENGTH;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('writeReview')}</DialogTitle>
          <DialogDescription>
            {t('shareExperienceAtPark', { parkName: park.name })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Star Rating */}
          <div className="space-y-2">
            <Label>{t('ratingRequired')}</Label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110 active:scale-95 cursor-pointer"
                >
                  <Star
                    className={cn(
                      'h-8 w-8 sm:h-10 sm:w-10',
                      star <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                    )}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm text-muted-foreground">
                  {rating} {rating === 1 ? t('starSingular') : t('starPlural')}
                </span>
              )}
            </div>
          </div>

          {/* Title (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('titleOptional')}</Label>
            <Input
              id="title"
              placeholder={t('reviewTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Review Content */}
          <div className="space-y-2">
            <Label htmlFor="content">{t('yourReviewRequired')}</Label>
            <Textarea
              id="content"
              placeholder={t('reviewContentPlaceholder')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {t('reviewMinimumCharacters', {
                  count: MIN_REVIEW_LENGTH.toString(),
                })}
              </span>
              <span
                className={cn(
                  characterCount > MAX_REVIEW_LENGTH && 'text-red-500'
                )}
              >
                {characterCount} / {MAX_REVIEW_LENGTH}
              </span>
            </div>
          </div>

          {/* Visit Date (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="visit-date">{t('visitDateOptional')}</Label>
            <Input
              id="visit-date"
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>{t('tagsOptional')}</Label>
            <div className="flex flex-wrap gap-2">
              {REVIEW_TAGS.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className="inline-flex items-center cursor-pointer"
                >
                  <Badge
                    variant={
                      selectedTags.includes(tag.id) ? 'default' : 'outline'
                    }
                    className="cursor-pointer text-xs transition-colors active:scale-95"
                  >
                    {tag.icon && <span className="mr-1">{tag.icon}</span>}
                    {getReviewTagLabel(tag.id, tag.label)}
                  </Badge>
                </button>
              ))}
            </div>
          </div>

          {/* Selected Pet */}
          {selectedPet && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm text-muted-foreground">
                {t('reviewingWith')}{' '}
                <span className="font-medium">{selectedPet.name}</span>
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 cursor-pointer"
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 cursor-pointer"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submitReview')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
